import React, { useRef, useEffect, memo } from 'react';
import { googleMapsInit, mapOptions, enableZoomControls, markerStyle } from '../../../utils/google-maps';
import { useQuery } from 'react-query';

const THEMEAPIS_URL = process.env.THEMEAPIS_URL ?? '';
const STATIC_MAP_SIZE = '640x451';

type StoreMapProps = {
  id: string;
  lat: number;
  lng: number;
  storeId: string;
  location?: string;
  className?: string;
};

type MapOptionsType = {
  zoom: number;
  center: any;
  disableDefaultUI?: boolean;
  gestureHandling?: string;
  mapTypeControl?: boolean;
  fullscreenControl?: boolean;
};

const StoreMap = memo(({ type = 'default', ...props }: StoreMapProps & { type: 'static' | 'default' }): JSX.Element => {
  if (type === 'static') return <StaticStoreMap {...props} />;
  return <DynamicStoreMap {...props} />;
});

StoreMap.displayName = 'StoreMap';

const DynamicStoreMap = ({ id, lat, lng, className }: StoreMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapRendered = useRef(false);

  const renderGoogleMap = () => {
    if (!window.google) return;

    const options: MapOptionsType = {
      zoom: 16,
      center: new window.google.maps.LatLng(lat, lng),
      mapTypeControl: false,
      fullscreenControl: false,
    };

    const mapConfig = mapOptions(options);
    const map = new window.google.maps.Map(mapRef.current, mapConfig);

    new window.google.maps.Marker(markerStyle({ position: { lat, lng }, map }, 'single'));

    enableZoomControls(map);
  };

  useEffect(() => {
    if (lat !== 0 && lng !== 0 && !mapRendered.current) {
      mapRendered.current = true;
      googleMapsInit(renderGoogleMap);
    }
  }, [lat, lng]);

  return <div id={id} ref={mapRef} className={className}></div>;
};

const StaticStoreMap = ({ id, storeId, className }: StoreMapProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ['getStoreStaticMap', { storeId, size: STATIC_MAP_SIZE }],
    queryFn: async () => {
      const response = await fetch(`${THEMEAPIS_URL}/getStoreStaticMap?storeId=${storeId}&size=${STATIC_MAP_SIZE}`);
      return (await response.json()) as { url: string };
    },
  });

  if (isLoading || !data?.url) return null;

  return <img id={id} className={className} src={data?.url} alt="Store map image" />;
};

export default StoreMap;
